import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Pencil, Lock, CalendarClock, Building2, GripVertical, MessageSquare, ListChecks, Send, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/helpers";
import { toast } from "sonner";

const LISTS = [
  { key: "todo", label: "To Do", accent: "border-t-[#3a4a6b]" },
  { key: "doing", label: "Doing", accent: "border-t-[#D98324]" },
  { key: "done", label: "Done", accent: "border-t-[#2B4C3F]" },
];

const PRIORITY = {
  low: { label: "Low", cls: "bg-[#2B4C3F]/12 text-[#2B4C3F]" },
  medium: { label: "Medium", cls: "bg-[#C6A87C]/20 text-[#8a6d3b]" },
  high: { label: "High", cls: "bg-[#9E382A]/12 text-[#9E382A]" },
};

const empty = { title: "", description: "", list: "todo", assignees: [], priority: "medium", due_date: "", property_id: null, is_private: false };

function TaskDialog({ trigger, initial, staff, properties, isAdmin, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || empty);
  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({ ...empty, ...initial, description: initial.description || "", due_date: initial.due_date || "", assignees: initial.assignees || [] });
      } else {
        setForm(empty);
      }
    }
  }, [open]);

  const toggleAssignee = (id) => {
    setForm((f) => ({ ...f, assignees: f.assignees.includes(id) ? f.assignees.filter((x) => x !== id) : [...f.assignees, id] }));
  };

  const save = async () => {
    try {
      const payload = {
        title: form.title, description: form.description, list: form.list,
        assignees: form.assignees, priority: form.priority,
        due_date: form.due_date || null,
        property_id: form.property_id || null,
        is_private: !!form.is_private,
      };
      if (initial?.id) await api.put(`/tasks/${initial.id}`, payload);
      else await api.post("/tasks", payload);
      toast.success("Saved");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-head">{initial?.id ? "Edit Task" : "New Task"}</DialogTitle>
          <DialogDescription className="sr-only">Create or edit a team task with priority, due date, assignees and an optional property tag.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto thin-scroll pr-1">
          <div><Label className="overline">Title</Label><Input data-testid="task-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="e.g. Chase gas cert" /></div>
          <div><Label className="overline">Description</Label><Textarea data-testid="task-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">List</Label>
              <Select value={form.list} onValueChange={(v) => setForm({ ...form, list: v })}>
                <SelectTrigger data-testid="task-list-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{LISTS.map((l) => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="task-priority-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(PRIORITY).map((p) => <SelectItem key={p} value={p}>{PRIORITY[p].label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Due date</Label><Input data-testid="task-due-input" type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" /></div>
            <div>
              <Label className="overline">Tag property</Label>
              <Select value={form.property_id || "none"} onValueChange={(v) => setForm({ ...form, property_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="task-property-select" className="mt-1.5"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No property</SelectItem>
                  {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="overline">Assign to</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-1.5 border border-border rounded-md p-2">
              {staff.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer py-0.5">
                  <Checkbox data-testid={`task-assignee-${s.id}`} checked={form.assignees.includes(s.id)} onCheckedChange={() => toggleAssignee(s.id)} />
                  <span className="truncate">{s.name}</span>
                </label>
              ))}
              {staff.length === 0 && <p className="text-xs text-muted-foreground col-span-2">No staff found.</p>}
            </div>
          </div>
          {isAdmin && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox data-testid="task-private-checkbox" checked={!!form.is_private} onCheckedChange={(v) => setForm({ ...form, is_private: !!v })} />
              <Lock className="h-3.5 w-3.5 text-muted-foreground" /> Private (only admins can see this card)
            </label>
          )}
        </div>
        <DialogFooter><Button data-testid="save-task-button" onClick={save} disabled={!form.title}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskDetailDialog({ task, isAdmin, onSaved, trigger }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [t, setT] = useState(task);
  const [sub, setSub] = useState("");
  const [comment, setComment] = useState("");
  useEffect(() => { if (open) setT(task); }, [open, task]);
  const refresh = (data) => { setT(data); onSaved(); };
  const run = async (fn) => { try { const { data } = await fn(); refresh(data); } catch (e) { toast.error(apiError(e)); } };
  const addSub = () => { const v = sub.trim(); if (!v) return; setSub(""); run(() => api.post(`/tasks/${t.id}/subtasks`, { text: v })); };
  const toggleSub = (s) => run(() => api.patch(`/tasks/${t.id}/subtasks/${s.id}`, { done: !s.done }));
  const delSub = (s) => run(() => api.delete(`/tasks/${t.id}/subtasks/${s.id}`));
  const addComment = () => { const v = comment.trim(); if (!v) return; setComment(""); run(() => api.post(`/tasks/${t.id}/comments`, { text: v })); };
  const delComment = (c) => run(() => api.delete(`/tasks/${t.id}/comments/${c.id}`));
  const subs = t.subtasks || [];
  const comments = t.comments || [];
  const done = subs.filter((s) => s.done).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-head break-words pr-6">{t.title}</DialogTitle>
          <DialogDescription className="sr-only">Task checklist and comment history.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 max-h-[68vh] overflow-y-auto thin-scroll pr-1">
          <div>
            <p className="overline flex items-center gap-1.5 mb-2"><ListChecks className="h-3.5 w-3.5" />Checklist {subs.length > 0 && <span className="text-muted-foreground">· {done}/{subs.length}</span>}</p>
            <div className="space-y-1.5">
              {subs.map((s) => (
                <div key={s.id} data-testid={`subtask-${s.id}`} className="flex items-center gap-2 group/sub">
                  <Checkbox checked={s.done} onCheckedChange={() => toggleSub(s)} data-testid={`subtask-check-${s.id}`} />
                  <span className={`text-sm flex-1 break-words ${s.done ? "line-through text-muted-foreground" : ""}`}>{s.text}</span>
                  <button onClick={() => delSub(s)} data-testid={`subtask-del-${s.id}`} className="opacity-0 group-hover/sub:opacity-100 transition-opacity"><X className="h-3.5 w-3.5 text-[#9E382A]" /></button>
                </div>
              ))}
              {subs.length === 0 && <p className="text-xs text-muted-foreground">No checklist items yet.</p>}
            </div>
            <div className="flex gap-2 mt-2">
              <Input data-testid="subtask-input" value={sub} onChange={(e) => setSub(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSub()} placeholder="Add a checklist item…" className="h-8 text-sm" />
              <Button size="sm" variant="outline" data-testid="add-subtask" onClick={addSub}><Plus className="h-4 w-4" /></Button>
            </div>
          </div>

          <div>
            <p className="overline flex items-center gap-1.5 mb-2"><MessageSquare className="h-3.5 w-3.5" />Comments {comments.length > 0 && <span className="text-muted-foreground">· {comments.length}</span>}</p>
            <div className="space-y-2">
              {comments.map((c) => (
                <div key={c.id} data-testid={`comment-${c.id}`} className="bg-secondary/40 border border-border rounded-md px-3 py-2 group/com">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold">{c.author_name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[0.65rem] text-muted-foreground">{fmtDate(c.created_at)}</span>
                      {(c.author_id === user.id || isAdmin) && <button onClick={() => delComment(c)} data-testid={`comment-del-${c.id}`} className="opacity-0 group-hover/com:opacity-100 transition-opacity"><X className="h-3 w-3 text-[#9E382A]" /></button>}
                    </div>
                  </div>
                  <p className="text-sm mt-1 whitespace-pre-wrap break-words">{c.text}</p>
                </div>
              ))}
              {comments.length === 0 && <p className="text-xs text-muted-foreground">No comments yet — start the thread.</p>}
            </div>
            <div className="flex gap-2 mt-2">
              <Textarea data-testid="comment-input" rows={1} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a comment…" className="text-sm min-h-[38px]" />
              <Button size="sm" data-testid="add-comment" onClick={addComment} disabled={!comment.trim()}><Send className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TaskCard({ task, staff, properties, isAdmin, onSaved, onDelete, onDragStart }) {
  const overdue = task.due_date && task.list !== "done" && new Date(task.due_date) < new Date(new Date().toDateString());
  const subs = task.subtasks || [];
  const comments = task.comments || [];
  const doneSub = subs.filter((s) => s.done).length;
  return (
    <motion.div
      layout
      draggable
      onDragStart={(e) => onDragStart(e, task)}
      data-testid={`task-card-${task.id}`}
      className="group bg-white border border-border rounded-md p-3 cursor-grab active:cursor-grabbing hover:border-[#C6A87C] transition-colors"
    >
      <div className="flex items-start gap-1.5">
        <GripVertical className="h-4 w-4 text-muted-foreground/40 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-[0.6rem] px-2 py-0.5 rounded-full font-medium ${PRIORITY[task.priority]?.cls}`}>{PRIORITY[task.priority]?.label}</span>
            {task.is_private && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#18181A] text-white flex items-center gap-1"><Lock className="h-2.5 w-2.5" />Private</span>}
          </div>
          <p className="text-sm font-semibold mt-1.5 break-words">{task.title}</p>
          {task.description && <p className="text-xs text-muted-foreground mt-0.5 break-words line-clamp-3">{task.description}</p>}
          {task.property_title && (
            <p className="text-xs text-[#3a4a6b] mt-1.5 flex items-center gap-1"><Building2 className="h-3 w-3" />{task.property_title}</p>
          )}
          {task.due_date && (
            <p className={`text-xs mt-1 flex items-center gap-1 ${overdue ? "text-[#9E382A] font-medium" : "text-muted-foreground"}`}>
              <CalendarClock className="h-3 w-3" />{fmtDate(task.due_date)}{overdue ? " · overdue" : ""}
            </p>
          )}
          {task.assignee_names?.length > 0 && (
            <div className="flex items-center gap-1 mt-2 flex-wrap">
              {task.assignee_names.map((n, i) => (
                <span key={i} className="h-6 w-6 rounded-full bg-[#2B4C3F]/15 text-[#2B4C3F] text-[0.6rem] font-bold flex items-center justify-center" title={n}>
                  {(n.replace(/[^a-zA-Z0-9 ]/g, "").split(/\s+/).filter(Boolean).map((x) => x[0]).slice(0, 2).join("") || "?").toUpperCase()}
                </span>
              ))}
            </div>
          )}
          {(subs.length > 0 || comments.length > 0) && (
            <div className="flex items-center gap-3 mt-2 text-[0.65rem] text-muted-foreground">
              {subs.length > 0 && <span className="flex items-center gap-1" data-testid={`card-subtask-count-${task.id}`}><ListChecks className="h-3 w-3" />{doneSub}/{subs.length}</span>}
              {comments.length > 0 && <span className="flex items-center gap-1" data-testid={`card-comment-count-${task.id}`}><MessageSquare className="h-3 w-3" />{comments.length}</span>}
            </div>
          )}
          <div className="flex items-center gap-1 mt-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
            <TaskDetailDialog task={task} isAdmin={isAdmin} onSaved={onSaved}
              trigger={<Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`task-details-${task.id}`}><MessageSquare className="h-3.5 w-3.5" /></Button>} />
            <TaskDialog initial={task} staff={staff} properties={properties} isAdmin={isAdmin} onSaved={onSaved}
              trigger={<Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`task-edit-${task.id}`}><Pencil className="h-3.5 w-3.5" /></Button>} />
            <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`task-delete-${task.id}`} onClick={() => onDelete(task)}><Trash2 className="h-3.5 w-3.5 text-[#9E382A]" /></Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function TaskManager() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [properties, setProperties] = useState([]);
  const [dragOver, setDragOver] = useState(null);
  const [mineOnly, setMineOnly] = useState(false);

  const load = () => api.get("/tasks").then((r) => setTasks(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
  }, []);

  const onDragStart = (e, task) => { e.dataTransfer.setData("text/plain", task.id); e.dataTransfer.effectAllowed = "move"; };
  const onDrop = async (e, listKey) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/plain");
    const task = tasks.find((t) => t.id === id);
    if (!task || task.list === listKey) return;
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, list: listKey } : t)));
    try { await api.patch(`/tasks/${id}/move`, { list: listKey }); }
    catch (err) { toast.error(apiError(err)); load(); }
  };
  const remove = async (task) => {
    try { await api.delete(`/tasks/${task.id}`); toast.success("Task deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-accent">Team board</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tight mt-1">Tasks</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={mineOnly ? "default" : "outline"} data-testid="my-tasks-toggle" onClick={() => setMineOnly((v) => !v)}>
            {mineOnly ? "My tasks" : "All tasks"}
          </Button>
          <TaskDialog staff={staff} properties={properties} isAdmin={isAdmin} onSaved={load}
            trigger={<Button data-testid="new-task-button"><Plus className="h-4 w-4 mr-1" />New Task</Button>} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {LISTS.map((l) => {
          const col = tasks.filter((t) => t.list === l.key && (!mineOnly || (t.assignees || []).includes(user.id)));
          return (
            <div
              key={l.key}
              data-testid={`task-column-${l.key}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(l.key); }}
              onDragLeave={() => setDragOver((d) => (d === l.key ? null : d))}
              onDrop={(e) => onDrop(e, l.key)}
              className={`bg-secondary/40 border border-border border-t-2 ${l.accent} rounded-md p-3 min-h-[200px] transition-colors ${dragOver === l.key ? "ring-2 ring-[#C6A87C] bg-[#C6A87C]/5" : ""}`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="overline text-[0.6rem]">{l.label}</span>
                <span className="text-xs font-mono text-muted-foreground">{col.length}</span>
              </div>
              <div className="space-y-2">
                {col.map((task) => (
                  <TaskCard key={task.id} task={task} staff={staff} properties={properties} isAdmin={isAdmin}
                    onSaved={load} onDelete={remove} onDragStart={onDragStart} />
                ))}
                {col.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">Drop tasks here</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
